import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Pill, FlaskConical, Layers, Eye, RefreshCw, History, ShoppingBag } from "lucide-react";
import { ProveedorCombobox } from "@/components/ProveedorCombobox";
import { useAuth } from "@/contexts/AuthContext";
import { Product360Dialog } from "@/components/Product360Dialog";
import LotesProductoDialog from "@/components/LotesProductoDialog";
import { onCatalogUpdated } from "@/lib/catalogSync";
import { ColumnPicker, useColumnPicker } from "@/components/ColumnPicker";
import HistorialVentasProductoDialog from "@/components/HistorialVentasProductoDialog";

export default function Products() {
  return (
    <div className="space-y-6" data-testid="products-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Maestros</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Productos
        </h1>
        <p className="text-sm text-[#4a5c53] mt-1 max-w-2xl">
          Los productos se gestionan en 3 niveles: <strong>Producto Base</strong> (identidad técnica) →{" "}
          <strong>Producto Comercial</strong> (variante con laboratorio, marca y precio) → <strong>Lotes</strong> (inventario real).
        </p>
      </div>

      <Tabs defaultValue="base" className="w-full">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="base" data-testid="tab-base"><FlaskConical size={14} className="mr-2" />Producto Base</TabsTrigger>
          <TabsTrigger value="comercial" data-testid="tab-comercial"><Pill size={14} className="mr-2" />Producto Comercial</TabsTrigger>
          <TabsTrigger value="lotes" data-testid="tab-lotes"><Layers size={14} className="mr-2" />Lotes</TabsTrigger>
        </TabsList>
        <TabsContent value="base"><ProductosBase /></TabsContent>
        <TabsContent value="comercial"><ProductosComerciales /></TabsContent>
        <TabsContent value="lotes"><Lotes /></TabsContent>
      </Tabs>
    </div>
  );
}

// ================= Producto Base =================
function ProductosBase() {
  const [items, setItems] = useState([]);
  const [formas, setFormas] = useState([]);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBase());
  function emptyBase() { return { principio_activo: "", forma_farmaceutica_id: "", concentracion: "", unidad_concentracion: "mg", controlado: false, requiere_cadena_frio: false, observaciones: "" }; }

  const load = async () => {
    try {
      const [{ data: pb }, { data: ff }] = await Promise.all([api.get("/masters/productos-base"), api.get("/masters/catalog/formas_farmaceuticas")]);
      setItems(pb); setFormas(ff);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Sincroniza formas_farmaceuticas si cambian en otra pestaña.
  useEffect(() => {
    const unsub = onCatalogUpdated(["formas_farmaceuticas"], () => { load(); });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    try {
      if (editing) await api.patch(`/masters/productos-base/${editing.id}`, form);
      else await api.post("/masters/productos-base", form);
      toast.success("Guardado"); setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.principio_activo}"?`)) return;
    try { await api.delete(`/masters/productos-base/${it.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const ffMap = Object.fromEntries(formas.map(f => [f.id, f.nombre]));
  const q = search.trim().toLowerCase();
  const visible = q ? items.filter(it => [
    it.principio_activo, ffMap[it.forma_farmaceutica_id], it.concentracion,
    it.unidad_concentracion, it.observaciones,
  ].filter(Boolean).join(" ").toLowerCase().includes(q)) : items;

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between items-center gap-3">
        <div className="flex-1 max-w-md relative">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por principio activo, forma, concentración…"
            className="pl-8"
            data-testid="base-search-input"
          />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8ba397]"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyBase()); setDialogOpen(true); }} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-base-btn">
          <Plus size={16} className="mr-2" /> Nuevo producto base
        </Button>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Principio activo</th><th>Forma farmacéutica</th><th>Concentración</th><th>Controlado</th><th>Cadena frío</th><th className="w-24"></th></tr></thead>
          <tbody>
            {visible.map(it => (
              <tr key={it.id}>
                <td className="font-medium">{it.principio_activo}</td>
                <td className="text-[#4a5c53]">{ffMap[it.forma_farmaceutica_id] || "—"}</td>
                <td>{it.concentracion ? `${it.concentracion} ${it.unidad_concentracion || ""}` : "—"}</td>
                <td>{it.controlado ? <Badge className="fefo-critico border">Sí</Badge> : <span className="text-[#8ba397]">No</span>}</td>
                <td>{it.requiere_cadena_frio ? <Badge variant="secondary">2-8°C</Badge> : <span className="text-[#8ba397]">No</span>}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(it); setForm({...emptyBase(), ...it}); setDialogOpen(true); }}><Edit2 size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 size={16} className="text-[#b53a25]" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8ba397]">{q ? `Sin resultados para "${search}"` : "Sin productos base aún"}</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} producto base</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Principio activo</Label><Input value={form.principio_activo} onChange={(e) => setForm({...form, principio_activo: e.target.value})} data-testid="base-principio-input" /></div>
            <div className="col-span-2">
              <Label>Forma farmacéutica</Label>
              <Select value={form.forma_farmaceutica_id} onValueChange={(v) => setForm({...form, forma_farmaceutica_id: v})}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>{formas.map(f => <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Concentración</Label><Input value={form.concentracion} onChange={(e) => setForm({...form, concentracion: e.target.value})} /></div>
            <div><Label>Unidad</Label><Input value={form.unidad_concentracion} onChange={(e) => setForm({...form, unidad_concentracion: e.target.value})} placeholder="mg, ml, UI..." /></div>
            <div className="flex items-center gap-2"><Checkbox checked={form.controlado} onCheckedChange={(v) => setForm({...form, controlado: !!v})} /><Label>Controlado</Label></div>
            <div className="flex items-center gap-2"><Checkbox checked={form.requiere_cadena_frio} onCheckedChange={(v) => setForm({...form, requiere_cadena_frio: !!v})} /><Label>Cadena de frío</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45] hover:bg-[#1a4a37]">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ================= Producto Comercial =================
function ProductosComerciales() {
  const { canSeeCosts: canSeeCostsFn } = useAuth();
  const canSeeCosts = canSeeCostsFn();
  const [items, setItems] = useState([]);
  const [bases, setBases] = useState([]);
  const [labs, setLabs] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [pres, setPres] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("nombre_comercial");
  const [sortDir, setSortDir] = useState("asc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [view360, setView360] = useState(null); // { id, nombre }
  const [lotesView, setLotesView] = useState(null); // { id, nombre }
  const [historialView, setHistorialView] = useState(null); // { id, nombre, codigo, items, loading }
  const [histVentasView, setHistVentasView] = useState(null); // { id, nombre, codigo }

  // === Configuración de columnas (Feb 2026) ===
  const allColumns = [
    { key: "codigo_interno", label: "Código", defaultVisible: true },
    { key: "nombre_comercial", label: "Nombre comercial", locked: true, defaultVisible: true },
    { key: "base", label: "Producto base", defaultVisible: true },
    { key: "marca", label: "Marca", defaultVisible: false },
    { key: "laboratorio", label: "Laboratorio", defaultVisible: true },
    { key: "presentacion", label: "Presentación", defaultVisible: true },
    { key: "unidades_por_presentacion", label: "Unidades / caja", defaultVisible: false },
    { key: "registro_sanitario", label: "Registro sanitario", defaultVisible: false },
    { key: "iva_porcentaje", label: "IVA %", defaultVisible: false },
    ...(canSeeCosts ? [
      { key: "precio_costo", label: "Costo", defaultVisible: true },
      { key: "margen", label: "Margen %", defaultVisible: false },
      { key: "precio_venta", label: "Precio venta", defaultVisible: true },
      { key: "pvp", label: "PVP", defaultVisible: false },
      { key: "precio_minimo", label: "Precio mínimo", defaultVisible: false },
    ] : []),
  ];
  const cols = useColumnPicker(`pc-${canSeeCosts ? "f" : "v"}`, allColumns);

  function emptyForm() { return { producto_base_id: "", laboratorio_id: "", marca_id: "", presentacion_id: "", codigo_interno: "", nombre_comercial: "", registro_sanitario: "", unidades_por_presentacion: 1, precio_costo: 0, margen: 0.3, precio_venta: 0, precio_caja_venta: null, pvp: null, pvf: null, precio_techo: null, precio_minimo: null, precio_minimo_caja: null, iva_porcentaje: 0, iva_exento: false, permite_fraccionamiento: false, permite_venta_unidad: true, permite_venta_presentacion: true, cadena_frio_override: null, controlado_override: null, activo: true, observaciones: "" }; }

  const openHistorial = async (it) => {
    setHistorialView({ id: it.id, nombre: it.nombre_comercial, codigo: it.codigo_interno, items: [], loading: true });
    try {
      const { data } = await api.get(`/masters/productos-comerciales/${it.id}/historial-precios`);
      setHistorialView((h) => h && { ...h, items: data, loading: false });
    } catch (e) {
      toast.error(formatApiError(e));
      setHistorialView((h) => h && { ...h, loading: false });
    }
  };

  const load = async () => {
    try {
      const [pc, pb, l, m, p] = await Promise.all([
        api.get("/masters/productos-comerciales"),
        api.get("/masters/productos-base"),
        api.get("/masters/catalog/laboratorios"),
        api.get("/masters/catalog/marcas"),
        api.get("/masters/catalog/presentaciones"),
      ]);
      setItems(pc.data); setBases(pb.data); setLabs(l.data); setMarcas(m.data); setPres(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Sincroniza catálogos: si cambian en otra pestaña/ruta, recargamos sin F5.
  useEffect(() => {
    const unsub = onCatalogUpdated(["laboratorios", "marcas", "presentaciones"], () => { load(); });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    try {
      const payload = {...form};
      // remove empty id refs to undefined
      ["laboratorio_id","marca_id","presentacion_id"].forEach(k => { if (!payload[k]) payload[k] = null; });
      if (editing) await api.patch(`/masters/productos-comerciales/${editing.id}`, payload);
      else await api.post("/masters/productos-comerciales", payload);
      toast.success("Guardado"); setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.nombre_comercial}"?`)) return;
    try { await api.delete(`/masters/productos-comerciales/${it.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const baseMap = Object.fromEntries(bases.map(b => [b.id, b.principio_activo]));
  const labMap = Object.fromEntries(labs.map(l => [l.id, l.nombre]));
  const marcaMap = Object.fromEntries(marcas.map(m => [m.id, m.nombre]));
  const presMap = Object.fromEntries(pres.map(p => [p.id, p.nombre]));
  const q = search.trim().toLowerCase();
  const filteredItems = q ? items.filter(it => [
    it.codigo_interno, it.nombre_comercial, baseMap[it.producto_base_id],
    labMap[it.laboratorio_id], marcaMap[it.marca_id], presMap[it.presentacion_id],
    it.registro_sanitario,
  ].filter(Boolean).join(" ").toLowerCase().includes(q)) : items;
  // Ordenamiento client-side
  const visible = React.useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (it) => {
      switch (sortBy) {
        case "codigo_interno": return (it.codigo_interno || "").toLowerCase();
        case "nombre_comercial": return (it.nombre_comercial || "").toLowerCase();
        case "base": return (baseMap[it.producto_base_id] || "").toLowerCase();
        case "laboratorio": return (labMap[it.laboratorio_id] || "").toLowerCase();
        case "presentacion": return (presMap[it.presentacion_id] || "").toLowerCase();
        case "precio_costo": return Number(it.precio_costo || 0);
        case "precio_venta": return Number(it.precio_venta || 0);
        default: return "";
      }
    };
    return [...filteredItems].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [filteredItems, sortBy, sortDir, baseMap, labMap, presMap]);
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };
  const sortIcon = (col) => sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between items-center gap-3">
        <div className="flex-1 max-w-md relative">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por SKU, nombre, marca, laboratorio, principio activo…"
            className="pl-8"
            data-testid="comercial-search-input"
          />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8ba397]"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); }} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-comercial-btn">
          <Plus size={16} className="mr-2" /> Nuevo producto comercial
        </Button>
        <ColumnPicker config={cols} testId="comercial-columns-picker" />
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr>
            {cols.isVisible("codigo_interno") && <th onClick={() => toggleSort("codigo_interno")} className="cursor-pointer select-none">Código{sortIcon("codigo_interno")}</th>}
            {cols.isVisible("nombre_comercial") && <th onClick={() => toggleSort("nombre_comercial")} className="cursor-pointer select-none">Nombre comercial{sortIcon("nombre_comercial")}</th>}
            {cols.isVisible("base") && <th onClick={() => toggleSort("base")} className="cursor-pointer select-none">Base{sortIcon("base")}</th>}
            {cols.isVisible("marca") && <th onClick={() => toggleSort("marca")} className="cursor-pointer select-none">Marca{sortIcon("marca")}</th>}
            {cols.isVisible("laboratorio") && <th onClick={() => toggleSort("laboratorio")} className="cursor-pointer select-none">Laboratorio{sortIcon("laboratorio")}</th>}
            {cols.isVisible("presentacion") && <th onClick={() => toggleSort("presentacion")} className="cursor-pointer select-none">Presentación{sortIcon("presentacion")}</th>}
            {cols.isVisible("unidades_por_presentacion") && <th className="text-right">Uds/caja</th>}
            {cols.isVisible("registro_sanitario") && <th>Reg. Sanitario</th>}
            {cols.isVisible("iva_porcentaje") && <th className="text-right">IVA %</th>}
            {canSeeCosts && cols.isVisible("precio_costo") && <th onClick={() => toggleSort("precio_costo")} className="text-right cursor-pointer select-none">Costo{sortIcon("precio_costo")}</th>}
            {canSeeCosts && cols.isVisible("margen") && <th className="text-right">Margen</th>}
            {canSeeCosts && cols.isVisible("precio_venta") && <th onClick={() => toggleSort("precio_venta")} className="text-right cursor-pointer select-none">Precio venta{sortIcon("precio_venta")}</th>}
            {canSeeCosts && cols.isVisible("pvp") && <th className="text-right">PVP</th>}
            {canSeeCosts && cols.isVisible("precio_minimo") && <th className="text-right">P. mínimo</th>}
            <th className="w-28"></th>
          </tr></thead>
          <tbody>
            {visible.map(it => (
              <tr key={it.id}>
                {cols.isVisible("codigo_interno") && <td className="font-mono text-xs">{it.codigo_interno}</td>}
                {cols.isVisible("nombre_comercial") && <td className="font-medium">{it.nombre_comercial}</td>}
                {cols.isVisible("base") && <td className="text-[#4a5c53]">{baseMap[it.producto_base_id] || "—"}</td>}
                {cols.isVisible("marca") && <td className="text-[#4a5c53]">{marcaMap[it.marca_id] || "—"}</td>}
                {cols.isVisible("laboratorio") && <td>{labMap[it.laboratorio_id] || "—"}</td>}
                {cols.isVisible("presentacion") && <td>{presMap[it.presentacion_id] || "—"}</td>}
                {cols.isVisible("unidades_por_presentacion") && <td className="text-right tabular-nums">{it.unidades_por_presentacion || 1}</td>}
                {cols.isVisible("registro_sanitario") && <td className="text-xs text-[#4a5c53]">{it.registro_sanitario || "—"}</td>}
                {cols.isVisible("iva_porcentaje") && <td className="text-right tabular-nums">{it.iva_exento ? "Exento" : `${it.iva_porcentaje || 0}%`}</td>}
                {canSeeCosts && cols.isVisible("precio_costo") && <td className="text-right tabular-nums">${(it.precio_costo || 0).toFixed(2)}</td>}
                {canSeeCosts && cols.isVisible("margen") && <td className="text-right tabular-nums">{((it.margen || 0) * 100).toFixed(0)}%</td>}
                {canSeeCosts && cols.isVisible("precio_venta") && <td className="text-right tabular-nums font-semibold">${(it.precio_venta || 0).toFixed(2)}</td>}
                {canSeeCosts && cols.isVisible("pvp") && <td className="text-right tabular-nums">{it.pvp != null ? `$${(+it.pvp).toFixed(2)}` : "—"}</td>}
                {canSeeCosts && cols.isVisible("precio_minimo") && <td className="text-right tabular-nums text-[#a55700]">{it.precio_minimo != null ? `$${(+it.precio_minimo).toFixed(2)}` : "—"}</td>}
                <td>
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" title="Vista 360°" onClick={() => setView360({ id: it.id, nombre: it.nombre_comercial })} data-testid={`view360-btn-${it.id}`}><Eye size={16} className="text-[#205c45]" /></Button>
                    <Button size="icon" variant="ghost" title="Lotes del producto" onClick={() => setLotesView({ id: it.id, nombre: it.nombre_comercial })} data-testid={`lotes-btn-${it.id}`}><Layers size={16} className="text-[#205c45]" /></Button>
                    <Button size="icon" variant="ghost" title="Historial de precios" onClick={() => openHistorial(it)} data-testid={`historial-btn-${it.id}`}><History size={16} className="text-[#205c45]" /></Button>
                    <Button size="icon" variant="ghost" title="Historial de ventas (clientes que lo compraron)" onClick={() => setHistVentasView({ id: it.id, nombre: it.nombre_comercial, codigo: it.codigo_interno })} data-testid={`hist-ventas-btn-${it.id}`}><ShoppingBag size={16} className="text-[#205c45]" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(it); setForm({...emptyForm(), ...it}); setDialogOpen(true); }}><Edit2 size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 size={16} className="text-[#b53a25]" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={cols.visibleCount + 1} className="text-center py-10 text-[#8ba397]">{q ? `Sin resultados para "${search}"` : "Sin productos comerciales aún. Cree primero un Producto Base."}</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} producto comercial</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Producto base</Label>
              <Select value={form.producto_base_id} onValueChange={(v) => setForm({...form, producto_base_id: v})}>
                <SelectTrigger data-testid="comercial-base-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>{bases.map(b => <SelectItem key={b.id} value={b.id}>{b.principio_activo} {b.concentracion ? `· ${b.concentracion}${b.unidad_concentracion}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Código interno</Label><Input value={form.codigo_interno} onChange={(e) => setForm({...form, codigo_interno: e.target.value})} data-testid="comercial-codigo-input" /></div>
            <div><Label>Nombre comercial</Label><Input value={form.nombre_comercial} onChange={(e) => setForm({...form, nombre_comercial: e.target.value})} /></div>
            <div>
              <Label>Laboratorio</Label>
              <Select value={form.laboratorio_id || ""} onValueChange={(v) => setForm({...form, laboratorio_id: v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{labs.map(l => <SelectItem key={l.id} value={l.id}>{l.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Marca</Label>
              <Select value={form.marca_id || ""} onValueChange={(v) => setForm({...form, marca_id: v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{marcas.map(m => <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Presentación</Label>
              <Select value={form.presentacion_id || ""} onValueChange={(v) => setForm({...form, presentacion_id: v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{pres.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Unidades por presentación</Label><Input type="number" value={form.unidades_por_presentacion} onChange={(e) => setForm({...form, unidades_por_presentacion: parseInt(e.target.value)||1})} /></div>
            <div><Label>Registro sanitario</Label><Input value={form.registro_sanitario} onChange={(e) => setForm({...form, registro_sanitario: e.target.value})} /></div>
            <div><Label>Costo</Label><Input type="number" step="0.01" value={form.precio_costo} onChange={(e) => setForm({...form, precio_costo: parseFloat(e.target.value)||0})} /></div>
            <div><Label>Margen (0 a 0.99)</Label><Input type="number" step="0.01" value={form.margen} onChange={(e) => setForm({...form, margen: parseFloat(e.target.value)||0})} /></div>
            <div><Label>Precio venta (opcional)</Label><Input type="number" step="0.01" value={form.precio_venta} onChange={(e) => {
              const val = parseFloat(e.target.value) || 0;
              const upp = parseInt(form.unidades_por_presentacion) || 1;
              // Sync bidireccional: al cambiar precio unidad, actualizar precio caja
              setForm({...form, precio_venta: val, precio_caja_venta: val > 0 && upp > 0 ? parseFloat((val * upp).toFixed(4)) : form.precio_caja_venta});
            }} placeholder="Auto-calculado" /></div>
            <div>
              <Label title="Precio explícito por caja/presentación. Si lo modificas, precio_venta unidad se recalcula = precio_caja / unidades_por_presentacion.">
                Precio caja (opcional)
              </Label>
              <Input
                type="number" step="0.0001"
                value={form.precio_caja_venta ?? ""}
                onChange={(e) => {
                  const val = e.target.value === "" ? null : parseFloat(e.target.value);
                  const upp = parseInt(form.unidades_por_presentacion) || 1;
                  // Sync bidireccional: al cambiar precio caja, actualizar precio unidad
                  const newPv = val !== null && val > 0 && upp > 0 ? parseFloat((val / upp).toFixed(4)) : form.precio_venta;
                  setForm({...form, precio_caja_venta: val, precio_venta: newPv});
                }}
                placeholder="Auto (unidad × upp)"
                data-testid="precio-caja-venta-input"
              />
            </div>

            {/* ====== Bloque Precios avanzados ====== */}
            <div className="col-span-3 mt-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c7368] font-semibold mb-1 flex items-center gap-2">
                <span>Precios avanzados (opcionales)</span>
                <span className="text-[#8ba397] normal-case font-normal tracking-normal">Si los dejas vacíos, NO se aplican validaciones.</span>
              </div>
            </div>
            <div>
              <Label title="Precio Venta al Público — referencia impresa en caja, P.S. en factura">
                PVP (Precio sugerido)
              </Label>
              <Input
                type="number" step="0.0001"
                value={form.pvp ?? ""}
                onChange={(e) => setForm({...form, pvp: e.target.value === "" ? null : parseFloat(e.target.value)})}
                placeholder="—"
                data-testid="pvp-input"
              />
            </div>
            <div>
              <Label title="Precio Venta a Farmacia (mayoreo)">
                PVF (Mayoreo)
              </Label>
              <Input
                type="number" step="0.0001"
                value={form.pvf ?? ""}
                onChange={(e) => setForm({...form, pvf: e.target.value === "" ? null : parseFloat(e.target.value)})}
                placeholder="—"
                data-testid="pvf-input"
              />
            </div>
            <div>
              <Label title="Precio techo regulatorio (MSP): genera alerta si el precio de venta lo supera">
                P. Techo (MSP)
              </Label>
              <Input
                type="number" step="0.0001"
                value={form.precio_techo ?? ""}
                onChange={(e) => setForm({...form, precio_techo: e.target.value === "" ? null : parseFloat(e.target.value)})}
                placeholder="—"
                data-testid="precio-techo-input"
              />
            </div>
            <div>
              <Label title="Piso de negociación por UNIDAD: ventas debajo requieren PIN de Gerencia">
                P. Mínimo unidad
              </Label>
              <Input
                type="number" step="0.0001"
                value={form.precio_minimo ?? ""}
                onChange={(e) => setForm({...form, precio_minimo: e.target.value === "" ? null : parseFloat(e.target.value)})}
                placeholder="—"
                data-testid="precio-minimo-input"
              />
            </div>
            <div>
              <Label title="Piso de negociación por CAJA: ventas debajo requieren PIN de Gerencia">
                P. Mínimo caja
              </Label>
              <Input
                type="number" step="0.0001"
                value={form.precio_minimo_caja ?? ""}
                onChange={(e) => setForm({...form, precio_minimo_caja: e.target.value === "" ? null : parseFloat(e.target.value)})}
                placeholder="—"
                data-testid="precio-minimo-caja-input"
              />
            </div>
            <div className="col-span-3 -mt-2">
              {(() => {
                const pv = parseFloat(form.precio_venta) || 0;
                const pmin = parseFloat(form.precio_minimo) || 0;
                const pt = parseFloat(form.precio_techo) || 0;
                if (pmin > 0 && pv > 0 && pmin > pv) {
                  return <p className="text-[11px] text-[#b53a25] flex items-center gap-1">⚠️ El precio mínimo (${pmin.toFixed(2)}) es mayor que el precio de venta (${pv.toFixed(2)}). Revisa que sea intencional.</p>;
                }
                if (pt > 0 && pv > 0 && pv > pt) {
                  return <p className="text-[11px] text-[#b53a25] flex items-center gap-1">⚠️ El precio de venta (${pv.toFixed(2)}) supera el precio techo regulatorio (${pt.toFixed(2)}).</p>;
                }
                return null;
              })()}
            </div>
            <div>
              <Label>IVA por defecto</Label>
              <Select value={form.iva_exento ? "exento" : String(form.iva_porcentaje)} onValueChange={(v) => {
                if (v === "exento") setForm({...form, iva_exento: true, iva_porcentaje: 0});
                else setForm({...form, iva_exento: false, iva_porcentaje: parseFloat(v)});
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="exento">Exento (medicamentos)</SelectItem>
                  <SelectItem value="0">0%</SelectItem>
                  <SelectItem value="5">5%</SelectItem>
                  <SelectItem value="12">12%</SelectItem>
                  <SelectItem value="15">15%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2"><Checkbox checked={form.permite_fraccionamiento} onCheckedChange={(v) => setForm({...form, permite_fraccionamiento: !!v})} /><Label>Permite fraccionamiento</Label></div>
            <div className="flex items-center gap-2"><Checkbox checked={form.permite_venta_unidad} onCheckedChange={(v) => setForm({...form, permite_venta_unidad: !!v})} /><Label>Venta por unidad</Label></div>
            <div className="flex items-center gap-2"><Checkbox checked={form.permite_venta_presentacion} onCheckedChange={(v) => setForm({...form, permite_venta_presentacion: !!v})} /><Label>Venta por presentación (caja)</Label></div>

            <div className="col-span-2 border-t pt-3 mt-2">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-2">Override flags regulatorios (esta variante)</div>
              <p className="text-[11px] text-[#5c7368] mb-2">Por defecto se hereda del producto base. Use estos campos sólo cuando esta variante difiere (ej: Eritropoyetina 2000 UI laboratorio A con cadena de frío vs laboratorio B sin cadena de frío).</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Cadena de frío</Label>
                  <Select value={form.cadena_frio_override === null || form.cadena_frio_override === undefined ? "heredar" : (form.cadena_frio_override ? "true" : "false")} onValueChange={(v) => setForm({...form, cadena_frio_override: v === "heredar" ? null : v === "true"})}>
                    <SelectTrigger data-testid="comercial-cadena-frio-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="heredar">Heredar de producto base</SelectItem>
                      <SelectItem value="true">Sí, requiere cadena de frío</SelectItem>
                      <SelectItem value="false">No requiere</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Controlado</Label>
                  <Select value={form.controlado_override === null || form.controlado_override === undefined ? "heredar" : (form.controlado_override ? "true" : "false")} onValueChange={(v) => setForm({...form, controlado_override: v === "heredar" ? null : v === "true"})}>
                    <SelectTrigger data-testid="comercial-controlado-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="heredar">Heredar de producto base</SelectItem>
                      <SelectItem value="true">Sí, controlado</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="save-comercial-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {view360 && (
        <Product360Dialog
          open={!!view360}
          onOpenChange={(v) => { if (!v) setView360(null); }}
          productoId={view360.id}
          productoNombre={view360.nombre}
        />
      )}
      {lotesView && (
        <LotesProductoDialog
          open={!!lotesView}
          onOpenChange={(v) => { if (!v) setLotesView(null); }}
          productoId={lotesView.id}
          productoNombre={lotesView.nombre}
        />
      )}

      <HistorialVentasProductoDialog
        producto={histVentasView}
        onClose={() => setHistVentasView(null)}
      />


      {/* Sheet: Histórico de precios */}
      <Sheet open={!!historialView} onOpenChange={(v) => { if (!v) setHistorialView(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#d1dbd6]">
            <SheetTitle className="flex items-center gap-2"><History size={18} className="text-[#205c45]" /> Histórico de precios</SheetTitle>
            <div className="text-sm text-[#5c7368]">
              <div className="font-semibold text-[#15241d]">{historialView?.nombre}</div>
              <div className="font-mono text-xs">{historialView?.codigo}</div>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* ITER175: pestañas Costos vs Precios de venta */}
            {!historialView?.loading && historialView?.items?.length > 0 && (
              <div className="mb-4 flex gap-1 border-b border-[#d1dbd6]">
                <button
                  onClick={() => setHistorialView(h => h && ({ ...h, tab: "todos" }))}
                  className={`px-3 py-1.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${(historialView.tab || "todos") === "todos" ? "border-[#205c45] text-[#205c45]" : "border-transparent text-[#5c7368] hover:text-[#15241d]"}`}
                  data-testid="hist-tab-todos"
                >Todos ({historialView.items.length})</button>
                <button
                  onClick={() => setHistorialView(h => h && ({ ...h, tab: "costos" }))}
                  className={`px-3 py-1.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${historialView.tab === "costos" ? "border-[#205c45] text-[#205c45]" : "border-transparent text-[#5c7368] hover:text-[#15241d]"}`}
                  data-testid="hist-tab-costos"
                >Costos ({historialView.items.filter(h => (h.precio_costo_nuevo ?? 0) !== (h.precio_costo_anterior ?? 0)).length})</button>
                <button
                  onClick={() => setHistorialView(h => h && ({ ...h, tab: "precios" }))}
                  className={`px-3 py-1.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${historialView.tab === "precios" ? "border-[#205c45] text-[#205c45]" : "border-transparent text-[#5c7368] hover:text-[#15241d]"}`}
                  data-testid="hist-tab-precios"
                >Precios de venta ({historialView.items.filter(h => (h.precio_venta_nuevo ?? 0) !== (h.precio_venta_anterior ?? 0)).length})</button>
              </div>
            )}
            {historialView?.loading && <div className="text-center text-[#8ba397] py-8">Cargando…</div>}
            {!historialView?.loading && historialView?.items?.length === 0 && (
              <div className="text-center text-[#8ba397] py-8">
                Aún no hay cambios registrados.<br/>
                <span className="text-xs">Los cambios futuros se registrarán automáticamente.</span>
              </div>
            )}
            {!historialView?.loading && historialView?.items?.length > 0 && (
              <ol className="relative border-l-2 border-[#d1dbd6] ml-3 space-y-4 pb-2">
                {historialView.items
                  .filter(h => {
                    const tab = historialView.tab || "todos";
                    if (tab === "costos") return (h.precio_costo_nuevo ?? 0) !== (h.precio_costo_anterior ?? 0);
                    if (tab === "precios") return (h.precio_venta_nuevo ?? 0) !== (h.precio_venta_anterior ?? 0);
                    return true;
                  })
                  .map((h, i) => {
                  const costoDiff = (h.precio_costo_nuevo ?? 0) - (h.precio_costo_anterior ?? 0);
                  const ventaDiff = (h.precio_venta_nuevo ?? 0) - (h.precio_venta_anterior ?? 0);
                  const colorMotivo = h.motivo === "creacion" ? "bg-[#205c45]" : "bg-[#4a7766]";
                  return (
                    <li key={h.id || i} className="ml-4 pl-2">
                      <span className={`absolute -left-[7px] w-3 h-3 rounded-full ${colorMotivo} ring-2 ring-white`} />
                      <div className="bg-white border border-[#d1dbd6] rounded-md p-3 shadow-sm">
                        <div className="flex items-center justify-between text-xs text-[#5c7368]">
                          <span>{(h.fecha || "").slice(0, 16).replace("T", " ")}</span>
                          <span className="capitalize px-2 py-0.5 rounded bg-[#e9f2ee] text-[#205c45] text-[10px] font-semibold">{h.motivo}</span>
                        </div>
                        <div className="text-xs text-[#5c7368] mb-2">por <b>{h.user_nombre}</b></div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div className="bg-[#f7f9f8] rounded p-2">
                            <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Costo</div>
                            <div className="font-semibold tabular-nums">
                              {h.precio_costo_anterior !== null && h.precio_costo_anterior !== undefined ? `$${h.precio_costo_anterior.toFixed(4)} → ` : ""}
                              <span className="text-[#15241d]">${(h.precio_costo_nuevo ?? 0).toFixed(4)}</span>
                            </div>
                            {h.precio_costo_anterior !== null && Math.abs(costoDiff) > 0.0001 && (
                              <div className={`text-[10px] ${costoDiff > 0 ? "text-[#b53a25]" : "text-[#205c45]"}`}>
                                {costoDiff > 0 ? "▲" : "▼"} ${Math.abs(costoDiff).toFixed(4)}
                              </div>
                            )}
                          </div>
                          <div className="bg-[#f7f9f8] rounded p-2">
                            <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Precio venta</div>
                            <div className="font-semibold tabular-nums">
                              {h.precio_venta_anterior !== null && h.precio_venta_anterior !== undefined ? `$${h.precio_venta_anterior.toFixed(4)} → ` : ""}
                              <span className="text-[#15241d]">${(h.precio_venta_nuevo ?? 0).toFixed(4)}</span>
                            </div>
                            {h.precio_venta_anterior !== null && Math.abs(ventaDiff) > 0.0001 && (
                              <div className={`text-[10px] ${ventaDiff > 0 ? "text-[#205c45]" : "text-[#b53a25]"}`}>
                                {ventaDiff > 0 ? "▲" : "▼"} ${Math.abs(ventaDiff).toFixed(4)}
                              </div>
                            )}
                          </div>
                          <div className="bg-[#f7f9f8] rounded p-2">
                            <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Margen</div>
                            <div className="font-semibold tabular-nums">
                              {h.margen_anterior !== null && h.margen_anterior !== undefined ? `${(h.margen_anterior * 100).toFixed(1)}% → ` : ""}
                              <span className="text-[#15241d]">{((h.margen_nuevo ?? 0) * 100).toFixed(1)}%</span>
                            </div>
                          </div>
                        </div>

                        {/* Feb 2026: Detalles de compra cuando motivo='compra' */}
                        {h.motivo === "compra" && (
                          <div className="mt-2 pt-2 border-t border-dashed border-[#d1dbd6] grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
                            {h.factura_compra_numero && (
                              <div>
                                <span className="text-[#5c7368]">Factura:</span>{" "}
                                <span className="font-mono font-semibold text-[#205c45]">{h.factura_compra_numero}</span>
                              </div>
                            )}
                            {h.proveedor_nombre && (
                              <div>
                                <span className="text-[#5c7368]">Proveedor:</span>{" "}
                                <span className="font-semibold">{h.proveedor_nombre}</span>
                              </div>
                            )}
                            {h.laboratorio_nombre && (
                              <div>
                                <span className="text-[#5c7368]">Laboratorio:</span>{" "}
                                <span className="font-semibold">{h.laboratorio_nombre}</span>
                              </div>
                            )}
                            {h.numero_lote && (
                              <div>
                                <span className="text-[#5c7368]">Lote:</span>{" "}
                                <span className="font-mono">{h.numero_lote}</span>
                              </div>
                            )}
                            {h.costo_compra_linea != null && (
                              <div>
                                <span className="text-[#5c7368]">Costo línea:</span>{" "}
                                <span className="tabular-nums font-semibold">${(+h.costo_compra_linea).toFixed(4)}</span>
                              </div>
                            )}
                            {h.cantidad_comprada != null && (
                              <div>
                                <span className="text-[#5c7368]">Cantidad:</span>{" "}
                                <span className="tabular-nums">{h.cantidad_comprada}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ================= Lotes =================
function Lotes() {
  const { canSeeCosts: canSeeCostsFn } = useAuth();
  const canSeeCosts = canSeeCostsFn();
  const [items, setItems] = useState([]);
  const [prods, setProds] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [provs, setProvs] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("producto");
  const [sortDir, setSortDir] = useState("asc");
  const [filtroStock, setFiltroStock] = useState("todos"); // todos | con_stock | sin_stock | vencidos
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  // === Configuración de columnas Lotes (Feb 2026) ===
  const lotesColumns = [
    { key: "producto", label: "Producto", locked: true, defaultVisible: true },
    { key: "laboratorio", label: "Laboratorio", defaultVisible: true },
    { key: "presentacion", label: "Presentación", defaultVisible: true },
    { key: "lote", label: "Lote", defaultVisible: true },
    { key: "bodega", label: "Bodega", defaultVisible: true },
    { key: "ubicacion", label: "Ubicación", defaultVisible: true },
    { key: "distribuidor", label: "Distribuidor", defaultVisible: false },
    { key: "fecha_elaboracion", label: "Fecha elaboración", defaultVisible: true },
    { key: "fecha_vencimiento", label: "Fecha vencimiento", defaultVisible: true },
    { key: "disponible", label: "Stock disponible", defaultVisible: true },
    ...(canSeeCosts ? [{ key: "costo", label: "Costo unitario", defaultVisible: false }] : []),
    { key: "precio_venta", label: "Precio venta", defaultVisible: true },
    { key: "estado", label: "Estado / Vencimiento", defaultVisible: true },
  ];
  const cols = useColumnPicker(`lotes-${canSeeCosts ? "f" : "v"}`, lotesColumns);

  function emptyForm() { return { producto_comercial_id: "", bodega_id: "", proveedor_id: "", numero_lote: "", fecha_elaboracion: "", fecha_vencimiento: "", cantidad_inicial: 0, cantidad_disponible: 0, ubicacion: "", costo_unitario: 0, precio_venta: 0, observaciones: "" }; }

  const load = async () => {
    try {
      const [l, p, b, pr] = await Promise.all([
        api.get("/masters/lotes"),
        api.get("/masters/productos-comerciales"),
        api.get("/masters/catalog/bodegas"),
        api.get("/masters/proveedores"),
      ]);
      setItems(l.data); setProds(p.data); setBodegas(b.data); setProvs(pr.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Sincroniza catálogos (bodegas) + recarga si cambian Laboratorios/Presentaciones
  // que se muestran a través de productos-comerciales.
  useEffect(() => {
    const unsub = onCatalogUpdated(["bodegas", "laboratorios", "presentaciones", "marcas"], () => { load(); });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    try {
      const payload = {...form};
      if (!payload.proveedor_id) payload.proveedor_id = null;
      // ITER140: si precio_venta es 0/vacío/null, NO lo enviamos → backend hereda del producto
      // (crear) o conserva el valor previo (editar).
      if (payload.precio_venta === null || payload.precio_venta === "" || Number(payload.precio_venta) === 0) {
        delete payload.precio_venta;
      }
      if (payload.costo_unitario === null || payload.costo_unitario === "" || Number(payload.costo_unitario) === 0) {
        delete payload.costo_unitario;
      }
      if (editing) await api.patch(`/masters/lotes/${editing.id}`, payload);
      else await api.post("/masters/lotes", payload);
      toast.success("Guardado"); setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm("¿Eliminar lote?")) return;
    try { await api.delete(`/masters/lotes/${it.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const prodMap = Object.fromEntries(prods.map(p => [p.id, {
    nombre: p.nombre_comercial,
    laboratorio: p.laboratorio_nombre || "—",
    presentacion: p.presentacion_nombre || p.presentacion || "—",
    codigo_interno: p.codigo_interno,
    unidades_por_caja: Math.max(1, parseInt(p.unidades_por_presentacion) || 1),
    precio_venta: p.precio_venta,
    precio_costo: p.precio_costo,
  }]));
  const bodMap = Object.fromEntries(bodegas.map(b => [b.id, b.nombre]));
  const provMap = Object.fromEntries(provs.map(p => [p.id, p.razon_social || p.nombre || p.nombre_comercial || "—"]));
  const today = new Date().toISOString().slice(0, 10);
  const daysUntil = (d) => Math.floor((new Date(d) - new Date(today)) / 86400000);
  const q = search.trim().toLowerCase();
  const filteredItems = items.filter(it => {
    const p = prodMap[it.producto_comercial_id] || {};
    // Filtro stock
    const cant = Number(it.cantidad_disponible || 0);
    const vencido = it.fecha_vencimiento && it.fecha_vencimiento < today;
    if (filtroStock === "con_stock" && cant <= 0) return false;
    if (filtroStock === "sin_stock" && cant > 0) return false;
    if (filtroStock === "vencidos" && !vencido) return false;
    if (!q) return true;
    return [
      it.numero_lote, p.nombre, p.laboratorio, p.presentacion, bodMap[it.bodega_id],
      it.ubicacion, it.fecha_vencimiento, it.observaciones,
    ].filter(Boolean).join(" ").toLowerCase().includes(q);
  });
  // Ordenamiento client-side
  const visible = React.useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (it) => {
      const p = prodMap[it.producto_comercial_id] || {};
      switch (sortBy) {
        case "producto": return (p.nombre || "").toLowerCase();
        case "laboratorio": return (p.laboratorio || "").toLowerCase();
        case "presentacion": return (p.presentacion || "").toLowerCase();
        case "lote": return (it.numero_lote || "").toLowerCase();
        case "bodega": return (bodMap[it.bodega_id] || "").toLowerCase();
        case "fecha_elaboracion": return it.fecha_elaboracion || "";
        case "fecha_vencimiento": return it.fecha_vencimiento || "";
        case "disponible": return Number(it.cantidad_disponible || 0);
        case "costo": return Number(it.costo_unitario || 0);
        case "precio_venta": {
          // Inheritance robusta: si el lote tiene 0/null/undefined, usa el del producto.
          const lv = Number(it.precio_venta);
          if (Number.isFinite(lv) && lv > 0) return lv;
          const pv = Number(p.precio_venta);
          return Number.isFinite(pv) ? pv : 0;
        }
        default: return "";
      }
    };
    return [...filteredItems].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [filteredItems, sortBy, sortDir, prodMap, bodMap]);
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };
  const sortIcon = (col) => sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  // Eliminar masivamente los lotes en stock = 0 (visibles según filtros actuales)
  const eliminarLotesEn0 = async () => {
    const enCero = visible.filter(it => Number(it.cantidad_disponible || 0) <= 0);
    if (enCero.length === 0) return toast.error("No hay lotes en 0 en la vista actual");
    if (!window.confirm(
      `¿Eliminar ${enCero.length} lote(s) con stock = 0?\n\n` +
      `Esto es PERMANENTE. Se eliminarán los lotes:\n` +
      enCero.slice(0, 8).map(l => `  · ${(prodMap[l.producto_comercial_id]||{}).nombre || "—"} · Lote ${l.numero_lote}`).join("\n") +
      (enCero.length > 8 ? `\n  · … y ${enCero.length - 8} más` : "") +
      `\n\n¿Continuar?`
    )) return;
    setBulkDeleting(true);
    let ok = 0, fail = 0;
    for (const l of enCero) {
      try { await api.delete(`/masters/lotes/${l.id}`); ok++; }
      catch (_) { fail++; }
    }
    setBulkDeleting(false);
    toast.success(`Eliminados ${ok} lote(s)${fail ? ` (${fail} fallaron)` : ""}`);
    load();
  };

  const [reconcileDialog, setReconcileDialog] = useState(null); // {desde, hasta, preview?, aplicando?}

  const ejecutarReconciliacion = async (aplicar) => {
    const d = reconcileDialog || {};
    if (!d.desde) return toast.error("Indique la fecha 'desde'");
    setReconcileDialog(rd => ({ ...rd, aplicando: aplicar }));
    const toastId = aplicar ? toast.loading("Aplicando descuentos en lotes…", { duration: Infinity }) : null;
    try {
      const params = new URLSearchParams({ desde: d.desde, aplicar: aplicar ? "true" : "false" });
      if (d.hasta) params.set("hasta", d.hasta);
      const { data } = await api.post(`/invoicing/facturas/reconciliar-stock?${params}`);
      if (aplicar) {
        toast.success(`✅ ${data.pendientes} factura(s) procesadas. ${data.con_problema} con problema. Stock actualizado.`, { id: toastId, duration: 6000 });
        setReconcileDialog(null);
        load();
      } else {
        setReconcileDialog(rd => ({ ...rd, preview: data, aplicando: false }));
      }
    } catch (e) {
      toast.error(formatApiError(e), { id: toastId, duration: 8000 });
      setReconcileDialog(rd => ({ ...rd, aplicando: false }));
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="flex-1 max-w-md relative">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nº de lote, producto, bodega, ubicación, fecha…"
            className="pl-8"
            data-testid="lotes-search-input"
          />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8ba397]"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            value={filtroStock}
            onChange={(e) => setFiltroStock(e.target.value)}
            className="h-9 border border-[#d1dbd6] rounded px-2 text-sm bg-white"
            data-testid="lotes-filter-stock"
          >
            <option value="todos">Todos los lotes</option>
            <option value="con_stock">Con stock (&gt; 0)</option>
            <option value="sin_stock">Sin stock (= 0)</option>
            <option value="vencidos">Vencidos</option>
          </select>
          {filtroStock === "sin_stock" && visible.length > 0 && (
            <Button
              variant="outline"
              onClick={eliminarLotesEn0}
              disabled={bulkDeleting}
              className="text-[#b53a25] border-[#b53a25] hover:bg-[#fdecea]"
              data-testid="bulk-delete-lotes-zero-btn"
            >
              <Trash2 size={14} className="mr-2" />
              {bulkDeleting ? "Eliminando…" : `Eliminar ${visible.length} en 0`}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setReconcileDialog({ desde: new Date(Date.now() - 30 * 86400000).toISOString().slice(0,10), hasta: new Date().toISOString().slice(0,10) })}
            data-testid="reconcile-stock-btn"
            title="Detectar facturas emitidas que no descontaron stock y aplicar el descuento retroactivamente"
          >
            <RefreshCw size={14} className="mr-2" /> Reconciliar con facturas
          </Button>
          <Button onClick={() => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); }} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-lote-btn">
            <Plus size={16} className="mr-2" /> Nuevo lote
          </Button>
          <ColumnPicker config={cols} testId="lotes-columns-picker" />
        </div>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr>
            {cols.isVisible("producto") && <th onClick={() => toggleSort("producto")} className="cursor-pointer select-none">Producto{sortIcon("producto")}</th>}
            {cols.isVisible("laboratorio") && <th onClick={() => toggleSort("laboratorio")} className="cursor-pointer select-none">Laboratorio{sortIcon("laboratorio")}</th>}
            {cols.isVisible("presentacion") && <th onClick={() => toggleSort("presentacion")} className="cursor-pointer select-none">Presentación{sortIcon("presentacion")}</th>}
            {cols.isVisible("lote") && <th onClick={() => toggleSort("lote")} className="cursor-pointer select-none">Lote{sortIcon("lote")}</th>}
            {cols.isVisible("bodega") && <th onClick={() => toggleSort("bodega")} className="cursor-pointer select-none">Bodega{sortIcon("bodega")}</th>}
            {cols.isVisible("ubicacion") && <th>Ubicación</th>}
            {cols.isVisible("distribuidor") && <th>Distribuidor</th>}
            {cols.isVisible("fecha_elaboracion") && <th onClick={() => toggleSort("fecha_elaboracion")} className="cursor-pointer select-none">Elaboración{sortIcon("fecha_elaboracion")}</th>}
            {cols.isVisible("fecha_vencimiento") && <th onClick={() => toggleSort("fecha_vencimiento")} className="cursor-pointer select-none">Vencimiento{sortIcon("fecha_vencimiento")}</th>}
            {cols.isVisible("disponible") && <th onClick={() => toggleSort("disponible")} className="text-right cursor-pointer select-none">Disponible{sortIcon("disponible")}</th>}
            {canSeeCosts && cols.isVisible("costo") && <th onClick={() => toggleSort("costo")} className="text-right cursor-pointer select-none">Costo{sortIcon("costo")}</th>}
            {cols.isVisible("precio_venta") && <th onClick={() => toggleSort("precio_venta")} className="text-right cursor-pointer select-none">Precio venta{sortIcon("precio_venta")}</th>}
            {cols.isVisible("estado") && <th>Estado</th>}
            <th className="w-24"></th>
          </tr></thead>
          <tbody>
            {visible.map(it => {
              const d = daysUntil(it.fecha_vencimiento);
              const vcStatus = d < 0 ? { cls: "fefo-vencido", txt: "Vencido" } : d <= 30 ? { cls: "fefo-critico", txt: `${d}d` } : { cls: "fefo-safe", txt: `${d}d` };
              const p = prodMap[it.producto_comercial_id] || {};
              const upc = p.unidades_por_caja || 1;
              const cant = parseInt(it.cantidad_disponible) || 0;
              const cajas = upc > 1 ? Math.floor(cant / upc) : 0;
              const sueltas = upc > 1 ? cant - cajas * upc : 0;
              const pv = (Number(it.precio_venta) > 0) ? it.precio_venta : p.precio_venta;
              const costoDisplay = (Number(it.costo_unitario) > 0) ? it.costo_unitario : (Number(p.precio_costo) > 0 ? p.precio_costo : null);
              return (
                <tr key={it.id}>
                  {cols.isVisible("producto") && <td className="font-medium">{p.nombre || "—"}</td>}
                  {cols.isVisible("laboratorio") && <td className="text-xs text-[#4a5c53]">{p.laboratorio || "—"}</td>}
                  {cols.isVisible("presentacion") && <td className="text-xs text-[#4a5c53]">{p.presentacion || "—"}</td>}
                  {cols.isVisible("lote") && <td className="font-mono text-xs">{it.numero_lote}</td>}
                  {cols.isVisible("bodega") && <td>{bodMap[it.bodega_id] || "—"}</td>}
                  {cols.isVisible("ubicacion") && <td className="text-xs text-[#4a5c53]">{it.ubicacion || "—"}</td>}
                  {cols.isVisible("distribuidor") && <td className="text-xs text-[#4a5c53]" data-testid={`lote-distribuidor-${it.id}`}>{provMap[it.proveedor_id] || "—"}</td>}
                  {cols.isVisible("fecha_elaboracion") && <td className="text-[#4a5c53]">{it.fecha_elaboracion || "—"}</td>}
                  {cols.isVisible("fecha_vencimiento") && <td>{it.fecha_vencimiento}</td>}
                  {cols.isVisible("disponible") && (
                    <td className="text-right tabular-nums whitespace-nowrap" data-testid={`lote-disp-${it.id}`}>
                      <span className="font-semibold">{cant}</span> <span className="text-[#5c7368] text-xs">uds</span>
                      {upc > 1 && (
                        <div className="text-[10px] text-[#205c45] leading-tight">
                          {cajas > 0 && <span><strong>{cajas}</strong> caja{cajas !== 1 ? "s" : ""}</span>}
                          {cajas > 0 && sueltas > 0 && <span className="text-[#5c7368]"> + </span>}
                          {sueltas > 0 && <span><strong>{sueltas}</strong> suelta{sueltas !== 1 ? "s" : ""}</span>}
                          {cajas === 0 && sueltas === 0 && <span className="text-[#5c7368]">·</span>}
                        </div>
                      )}
                    </td>
                  )}
                  {canSeeCosts && cols.isVisible("costo") && (
                    <td className="text-right tabular-nums" data-testid={`lote-costo-${it.id}`}>
                      {costoDisplay != null ? `$${(+costoDisplay).toFixed(4)}` : "—"}
                    </td>
                  )}
                  {cols.isVisible("precio_venta") && (
                    <td className="text-right tabular-nums font-semibold" data-testid={`lote-pv-${it.id}`}>{pv != null ? `$${(+pv).toFixed(4)}` : "—"}</td>
                  )}
                  {cols.isVisible("estado") && <td><Badge className={`${vcStatus.cls} border`}>{vcStatus.txt}</Badge></td>}
                  <td>
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(it); setForm({...emptyForm(), ...it}); setDialogOpen(true); }}><Edit2 size={16} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 size={16} className="text-[#b53a25]" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && <tr><td colSpan={cols.visibleCount + 1} className="text-center py-10 text-[#8ba397]">{q ? `Sin resultados para "${search}"` : "Sin lotes aún"}</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} lote</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Producto comercial</Label>
              <Select value={form.producto_comercial_id} onValueChange={(v) => setForm({...form, producto_comercial_id: v})}>
                <SelectTrigger data-testid="lote-producto-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>{prods.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre_comercial} ({p.codigo_interno})</SelectItem>)}</SelectContent>
              </Select>
              {form.producto_comercial_id && (() => {
                const p = prodMap[form.producto_comercial_id];
                if (!p) return null;
                return (
                  <div className="text-xs text-[#5c7368] mt-1 flex flex-wrap gap-x-3 gap-y-1" data-testid="lote-prod-info">
                    <span><strong className="text-[#15241d]">Laboratorio:</strong> {p.laboratorio}</span>
                    <span><strong className="text-[#15241d]">Presentación:</strong> {p.presentacion}</span>
                    <span><strong className="text-[#15241d]">Código:</strong> <span className="font-mono">{p.codigo_interno}</span></span>
                  </div>
                );
              })()}
            </div>
            <div>
              <Label>Bodega</Label>
              <Select value={form.bodega_id} onValueChange={(v) => setForm({...form, bodega_id: v})}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>{bodegas.map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Proveedor</Label>
              <ProveedorCombobox
                value={form.proveedor_id || ""}
                onChange={(v) => setForm({...form, proveedor_id: v})}
                proveedores={provs}
                placeholder="Buscar proveedor…"
                testId="lote-prov"
              />
            </div>
            <div><Label>Número de lote</Label><Input value={form.numero_lote} onChange={(e) => setForm({...form, numero_lote: e.target.value})} data-testid="lote-numero-input" /></div>
            <div><Label>Ubicación</Label><Input value={form.ubicacion} onChange={(e) => setForm({...form, ubicacion: e.target.value})} /></div>
            <div><Label>Fecha elaboración</Label><Input type="date" value={form.fecha_elaboracion} onChange={(e) => setForm({...form, fecha_elaboracion: e.target.value})} /></div>
            <div><Label>Fecha vencimiento</Label><Input type="date" value={form.fecha_vencimiento} onChange={(e) => setForm({...form, fecha_vencimiento: e.target.value})} data-testid="lote-vencimiento-input" /></div>
            <div><Label>Cantidad inicial</Label><Input type="number" value={form.cantidad_inicial} onChange={(e) => setForm({...form, cantidad_inicial: parseInt(e.target.value)||0, cantidad_disponible: parseInt(e.target.value)||0})} /></div>
            <div><Label>Disponible</Label><Input type="number" value={form.cantidad_disponible} onChange={(e) => setForm({...form, cantidad_disponible: parseInt(e.target.value)||0})} /></div>
            <div><Label>Costo unitario</Label><Input type="number" step="0.01" value={form.costo_unitario} onChange={(e) => setForm({...form, costo_unitario: parseFloat(e.target.value)||0})} /></div>
            <div>
              <Label>Precio venta</Label>
              <Input
                type="number"
                step="0.0001"
                value={form.precio_venta ?? ""}
                onChange={(e) => setForm({...form, precio_venta: e.target.value === "" ? null : parseFloat(e.target.value)})}
                placeholder="Hereda del producto"
                data-testid="lote-precio-venta-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="save-lote-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Reconciliar Stock con facturas históricas */}
      <Dialog open={!!reconcileDialog} onOpenChange={(v) => !v && setReconcileDialog(null)}>
        <DialogContent className="max-w-3xl" data-testid="reconcile-dialog">
          <DialogHeader>
            <DialogTitle>Reconciliar stock con facturas emitidas</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#4a5c53]">
            Detecta facturas autorizadas/firmadas que <strong>NO descontaron stock</strong> y aplica el descuento retroactivamente
            usando FEFO. Solo procesa facturas sin movimientos de stock previos (no duplica).
          </p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <Label>Desde *</Label>
              <Input type="date" value={reconcileDialog?.desde || ""} onChange={(e) => setReconcileDialog(rd => ({ ...rd, desde: e.target.value, preview: null }))} data-testid="reconcile-desde" />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input type="date" value={reconcileDialog?.hasta || ""} onChange={(e) => setReconcileDialog(rd => ({ ...rd, hasta: e.target.value, preview: null }))} data-testid="reconcile-hasta" />
            </div>
          </div>
          {reconcileDialog?.preview && (
            <div className="mt-3 border rounded-md p-3 bg-[#f7f9f8]">
              <div className="grid grid-cols-4 gap-2 text-center mb-3">
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Total procesadas</div><div className="text-xl font-bold">{reconcileDialog.preview.facturas_procesadas}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Ya tienen stock</div><div className="text-xl font-bold text-[#205c45]">{reconcileDialog.preview.ya_descontadas}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">A aplicar</div><div className="text-xl font-bold text-[#8a5500]">{reconcileDialog.preview.pendientes}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Con problema</div><div className="text-xl font-bold text-[#b53a25]">{reconcileDialog.preview.con_problema}</div></div>
              </div>
              <div className="max-h-[35vh] overflow-y-auto">
                <table className="erp-table text-xs">
                  <thead><tr><th>Factura</th><th>Fecha</th><th>Cliente</th><th>Líneas / Problema</th><th>Estado</th></tr></thead>
                  <tbody>
                    {(reconcileDialog.preview.detalles || []).map((d, i) => (
                      <tr key={i} className={d.estado === "error" ? "bg-[#fdecea]" : ""}>
                        <td className="font-mono">{d.factura}</td>
                        <td>{d.fecha || "—"}</td>
                        <td className="max-w-[180px] truncate" title={d.cliente}>{d.cliente || "—"}</td>
                        <td>{d.problema || `${d.lineas} línea(s)`}</td>
                        <td>
                          {d.estado === "lista_para_aplicar" ? <Badge className="bg-[#fef7ec] text-[#8a5500] border border-[#e9d3a3]">Aplicar</Badge> :
                           d.estado === "aplicada" ? <Badge className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]">Aplicada</Badge> :
                           <Badge className="bg-[#fdecea] text-[#b53a25] border border-[#e6b8ab]">Error</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {reconcileDialog.preview.con_problema > 0 && (
                <p className="text-[11px] text-[#b53a25] mt-2">
                  ⚠ Las facturas con problema NO se aplicarán. Corrija el stock manualmente o cree los lotes correspondientes.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconcileDialog(null)}>Cerrar</Button>
            {!reconcileDialog?.preview ? (
              <Button onClick={() => ejecutarReconciliacion(false)} disabled={!reconcileDialog?.desde || reconcileDialog?.aplicando} className="bg-[#205c45]" data-testid="reconcile-preview-btn">
                {reconcileDialog?.aplicando ? "Analizando…" : "Analizar (sin aplicar)"}
              </Button>
            ) : (
              <Button onClick={() => ejecutarReconciliacion(true)} disabled={reconcileDialog.aplicando || reconcileDialog.preview.pendientes === 0} className="bg-[#205c45]" data-testid="reconcile-apply-btn">
                {reconcileDialog.aplicando ? "Aplicando…" : `Aplicar ${reconcileDialog.preview.pendientes} descuento(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
